import {
  State,
  StartInterviewAction,
  ChangeInterviewNameAction,
  AnswerQuestionAction,
  AbandonInterviewAction,
  ConfirmAbandonInterviewAction,
  INTERVIEW_LOCAL_STORAGE_KEY
} from "./useInterview";
import {
  storeToLocalStorage,
  deleteFromLocalStorage
} from "../utils/localStorageHelpers";
import { findQuestion } from "./helpers";
import { ResultPanel } from "./ResultPanel";
import { Choice } from "./Choice";
import { flatMap } from "../utils/flatMap";

export function startInterview(state: State, action: StartInterviewAction) {
  const interview = action.interview;
  if (!interview) {
    throw new Error("An interview must be specified to start.");
  }
  return { ...state, interviewStarted: true, interview };
}

export function changeInterviewName(
  state: State,
  action: ChangeInterviewNameAction
) {
  const newState = JSON.parse(JSON.stringify(state)) as State;
  if (!newState.interviewStarted || !newState.interview) {
    throw new Error(
      "Interview must be started before questions can be answered!"
    );
  }

  newState.interview.interviewName = action.interviewName;

  // Now persist the new interview state to localStorage
  storeToLocalStorage<State>(INTERVIEW_LOCAL_STORAGE_KEY, newState);

  return newState;
}

export function answerQuestion(state: State, action: AnswerQuestionAction) {
  const aqi = action.actionParams;
  if (!aqi) {
    throw new Error("No answered question information provided!");
  }

  const newState = JSON.parse(JSON.stringify(state)) as State;

  if (!newState.interviewStarted || !newState.interview) {
    throw new Error(
      "Interview must be started before questions can be answered!"
    );
  }

  const answeredTopic = newState.interview.topics.find(
    t => t.topicID.toLowerCase() === aqi.topicId.toLowerCase()
  )!;
  const answeredQuestion = answeredTopic.questions.find(
    q => q.questionID.toLowerCase() === aqi.questionId.toLowerCase()
  )!;
  const chosenChoice = answeredQuestion.choices.find(
    c => c.choiceID.toLowerCase() === aqi.choiceId.toLowerCase()
  )!;

  answeredQuestion.choices.forEach(c => (c.selected = false));

  // When a question is answered, reset all questions below it in this topic
  let isBelow: boolean = false;
  answeredTopic.questions.forEach(q => {
    if (q.questionID === answeredQuestion.questionID) {
      isBelow = true;
    }
    if (isBelow) {
      // Reset
      q.isAnswered = false;
      q.isCurrentQuestion = false;
      q.comment = "";
      q.choices.forEach(c => {
        c.selected = false;

        // Remove any results that came from the downstream choices
        c.resultPanels.forEach(rp => {
          rp.results.forEach(result => {
            if (!newState.interview) return;

            const toRemoveIndex = newState.interview.resultPanels[
              rp.resultPanelName
            ].results.findIndex(
              r => r.resultID.toLowerCase() === result.resultID.toLowerCase()
            );
            if (toRemoveIndex !== -1) {
              newState.interview.resultPanels[
                rp.resultPanelName
              ].results.splice(toRemoveIndex, 1);
            }
          });
        });
      });
    }
  });

  // set the question as answered
  answeredQuestion.isAnswered = true;
  answeredQuestion.isCurrentQuestion = false;

  // set the correct choice to answered
  answeredQuestion.choices.forEach(c => (c.selected = false));
  chosenChoice.selected = true;

  // Set this topic to be in-progress if not already there
  newState.interview.resultPanels.NOTCOMP.results.push({
    resultID: answeredTopic.topicID,
    resultText: answeredTopic.shortName,
    embeddedLinks: [],
    resultOrder: 0
  });

  if (!chosenChoice.skipEvaluation) {
    const { questions } = answeredTopic;

    // Grab all answered questions
    const answeredQuestions = questions.filter(q => q.isAnswered);

    // grab all the selected choices from all the answered questions

    const selectedChoices: Choice[] = flatMap(
      answeredQuestions,
      q => q.choices
    ).filter(c => c.selected);

    const selectedChoiceResultPanels: ResultPanel[] = flatMap(
      selectedChoices,
      c => c.resultPanels
    );

    // get the top priority result panels from the selected choices
    const minPanelPrecedent: number = Math.min(
      ...selectedChoiceResultPanels.map(rp => rp.panelPrecedence)
    );
    const query = selectedChoiceResultPanels.filter(
      rp => rp.panelPrecedence === minPanelPrecedent
    );

    //exit out if results are not known yet.
    if (query.length > 0) {
      // grab the top 1 which is the highest precedence panel from all answered
      // questions and selected choices.
      const min: number = query[0].panelPrecedence;

      for (const q of questions) {
        for (const c of q.choices) {
          for (const r of c.resultPanels) {
            if (q.isAnswered && c.selected && r.panelPrecedence === min) {
              const current: ResultPanel =
                newState.interview.resultPanels[r.resultPanelName];

              if (current) {
                if (!current.results) {
                  current.results = [];
                }

                for (const result of r.results) {
                  if (
                    !current.results.find(
                      x =>
                        x.resultID.toLowerCase() ===
                        result.resultID.toLowerCase()
                    )
                  ) {
                    current.results.push(result);
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  //Did the current choice end the topic?
  if (chosenChoice.endTopic) {
    //assuming a NOT COMPLETE panel exists, remove this topic from it now that
    //it is done.
    if (
      newState.interview.resultPanels.NOTCOMP.results.find(
        r => r.resultID.toLowerCase() === answeredTopic.topicID.toLowerCase()
      )
    ) {
      const notCompResults = newState.interview.resultPanels.NOTCOMP.results;
      newState.interview.resultPanels.NOTCOMP.results = notCompResults.filter(
        r => r.resultID.toLowerCase() !== answeredTopic.topicID.toLowerCase()
      );
    }
  } else if (chosenChoice.nextQuestion) {
    // Set the next question to be answered
    findQuestion(
      answeredTopic.questions,
      chosenChoice.nextQuestion
    )!.isCurrentQuestion = true;
  }

  // TODO: Remove any results from "not declarable" if it's also in the "declarable"

  // Sort the result panels and remove duplicates
  const alreadyExists: { [key: string]: boolean } = {};

  [
    newState.interview.resultPanels.DEC,
    newState.interview.resultPanels.NOT,
    newState.interview.resultPanels.NOTCOMP
  ].forEach(rp => {
    rp.results = rp.results
      .sort((a, b) => a.resultID.localeCompare(b.resultID))
      .filter((element, index, array) => {
        if (element.resultID in alreadyExists) {
          return false;
        } else {
          alreadyExists[element.resultID] = true;
          return true;
        }
      });
  });

  // Now persist the new interview state to localStorage
  storeToLocalStorage<State>(INTERVIEW_LOCAL_STORAGE_KEY, newState);

  return newState;
}

export function abandonInterview(state: State, action: AbandonInterviewAction) {
  return {
    ...state,
    abandonInterviewRequested: true
  };
}

export function confirmAbandonInterview(
  state: State,
  action: ConfirmAbandonInterviewAction
) {
  if (action.reallyAbandon !== undefined && action.reallyAbandon) {
    deleteFromLocalStorage(INTERVIEW_LOCAL_STORAGE_KEY);
    return {
      ...state,
      interview: undefined,
      abandonInterviewRequested: false,
      interviewStarted: false
    };
  } else {
    return {
      ...state,
      abandonInterviewRequested: false
    };
  }
}
